/**
 * LeaveCalendarPage - Calendar view for team leaves
 * - Monthly and weekly views
 * - Managers see their team's leaves
 * - HR/Admin see all leaves
 */
import { useEffect, useState, useMemo } from 'react';
import { leaveAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  Users, Clock, Loader2 
} from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths,
  addWeeks, subWeeks, isWithinInterval, parseISO
} from 'date-fns';

// Color palette for different employees
const COLORS = [
  'bg-blue-100 text-blue-800 border-blue-300',
  'bg-green-100 text-green-800 border-green-300',
  'bg-purple-100 text-purple-800 border-purple-300',
  'bg-orange-100 text-orange-800 border-orange-300',
  'bg-pink-100 text-pink-800 border-pink-300',
  'bg-teal-100 text-teal-800 border-teal-300',
  'bg-indigo-100 text-indigo-800 border-indigo-300',
  'bg-yellow-100 text-yellow-800 border-yellow-300',
];

// Get a consistent color for an employee
const getEmployeeColor = (employeeId, colorMap) => {
  if (!colorMap[employeeId]) {
    const usedColors = Object.values(colorMap);
    const availableColors = COLORS.filter(c => !usedColors.includes(c));
    colorMap[employeeId] = availableColors.length > 0 
      ? availableColors[0] 
      : COLORS[Object.keys(colorMap).length % COLORS.length];
  }
  return colorMap[employeeId];
};

export default function LeaveCalendarPage() {
  const { user, isAdmin, isSuperAdmin, isHR } = useAuth();
  const canSeeAll = isAdmin || isSuperAdmin || isHR;
  
  const [view, setView] = useState('monthly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamOnly, setTeamOnly] = useState(true);
  const [colorMap] = useState({});
  
  // Fetch calendar data
  useEffect(() => {
    const fetchCalendar = async () => {
      setLoading(true);
      try {
        const params = {
          year: currentDate.getFullYear(),
          month: currentDate.getMonth() + 1,
          view,
          team_only: teamOnly
        };
        
        if (view === 'weekly') {
          params.week_start = format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        }
        
        const res = await leaveAPI.getCalendar(params);
        setEvents(res.data.events || []);
      } catch (error) {
        toast.error('Failed to load calendar');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCalendar();
  }, [currentDate, view, teamOnly]);
  
  // Navigation handlers
  const goToPrevious = () => {
    if (view === 'monthly') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };
  
  const goToNext = () => {
    if (view === 'monthly') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Generate calendar days based on view
  const calendarDays = useMemo(() => {
    if (view === 'monthly') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: calStart, end: calEnd });
    } else {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    }
  }, [currentDate, view]);
  
  // Get events for a specific day
  const getEventsForDay = (day) => {
    return events.filter(event => {
      const startDate = parseISO(event.start_date);
      const endDate = parseISO(event.end_date);
      return isWithinInterval(day, { start: startDate, end: endDate }) || 
             isSameDay(day, startDate) || 
             isSameDay(day, endDate);
    });
  };
  
  // Render calendar header
  const renderHeader = () => {
    const headerFormat = view === 'monthly' 
      ? format(currentDate, 'MMMM yyyy')
      : `Week of ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d')} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}`;
    
    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold font-['Manrope']">{headerFormat}</h1>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={goToPrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button variant="outline" size="icon" onClick={goToNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {canSeeAll && (
            <Select value={teamOnly ? 'team' : 'all'} onValueChange={(v) => setTeamOnly(v === 'team')}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="team">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> My Team
                  </span>
                </SelectItem>
                <SelectItem value="all">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> All Employees
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
          
          <Tabs value={view} onValueChange={setView} className="w-auto">
            <TabsList>
              <TabsTrigger value="monthly">
                <CalendarIcon className="w-4 h-4 mr-2" /> Month
              </TabsTrigger>
              <TabsTrigger value="weekly">
                <Clock className="w-4 h-4 mr-2" /> Week
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
    );
  };
  
  // Render day names header
  const renderDayNames = () => {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return (
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map(day => (
          <div 
            key={day} 
            className="text-center text-sm font-medium text-muted-foreground py-2"
          >
            {day}
          </div>
        ))}
      </div>
    );
  };
  
  // Render monthly calendar grid
  const renderMonthlyCalendar = () => {
    return (
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day, idx) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isToday = isSameDay(day, new Date());
          
          return (
            <div
              key={idx}
              className={`
                min-h-[100px] p-2 border rounded-lg transition-colors
                ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                ${isToday ? 'border-primary border-2' : 'border-gray-200'}
              `}
            >
              <div className={`
                text-sm font-medium mb-1
                ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                ${isToday ? 'text-primary' : ''}
              `}>
                {format(day, 'd')}
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event, eventIdx) => (
                  <div
                    key={event.id + eventIdx}
                    className={`
                      text-xs px-2 py-1 rounded border truncate cursor-default
                      ${getEmployeeColor(event.employee_id, colorMap)}
                    `}
                    title={`${event.employee_name}${event.is_half_day ? ` (${event.half_day_session === 'first_half' ? 'AM' : 'PM'})` : ''}`}
                  >
                    {event.employee_name}
                    {event.is_half_day && (
                      <span className="ml-1 text-[10px] opacity-75">
                        ({event.half_day_session === 'first_half' ? 'AM' : 'PM'})
                      </span>
                    )}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-xs text-muted-foreground text-center">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  
  // Render weekly calendar
  const renderWeeklyCalendar = () => {
    return (
      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((day, idx) => {
          const dayEvents = getEventsForDay(day);
          const isToday = isSameDay(day, new Date());
          
          return (
            <Card 
              key={idx}
              className={`${isToday ? 'border-primary border-2' : ''}`}
            >
              <CardHeader className="py-3 px-4">
                <CardTitle className={`text-lg ${isToday ? 'text-primary' : ''}`}>
                  {format(day, 'EEE')}
                  <span className="ml-2 font-normal text-muted-foreground">
                    {format(day, 'd MMM')}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2 px-4 space-y-2">
                {dayEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No leaves</p>
                ) : (
                  dayEvents.map((event, eventIdx) => (
                    <div
                      key={event.id + eventIdx}
                      className={`
                        p-2 rounded-lg border
                        ${getEmployeeColor(event.employee_id, colorMap)}
                      `}
                    >
                      <p className="font-medium text-sm">{event.employee_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {event.is_half_day 
                            ? `Half Day (${event.half_day_session === 'first_half' ? 'AM' : 'PM'})`
                            : `${event.days_count} day${event.days_count > 1 ? 's' : ''}`
                          }
                        </Badge>
                      </div>
                      {event.reason && (
                        <p className="text-xs mt-1 text-gray-600 truncate" title={event.reason}>
                          {event.reason}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };
  
  // Render legend
  const renderLegend = () => {
    const uniqueEmployees = [...new Map(events.map(e => [e.employee_id, e])).values()];
    
    if (uniqueEmployees.length === 0) return null;
    
    return (
      <Card className="mt-6">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium">Team Members on Leave</CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-wrap gap-2">
            {uniqueEmployees.map(emp => (
              <Badge
                key={emp.employee_id}
                variant="outline"
                className={`${getEmployeeColor(emp.employee_id, colorMap)}`}
              >
                {emp.employee_name}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div data-testid="leave-calendar-page" className="space-y-6 animate-fadeIn">
      {renderHeader()}
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {renderDayNames()}
          
          {view === 'monthly' ? renderMonthlyCalendar() : renderWeeklyCalendar()}
          
          {renderLegend()}
          
          {events.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No approved leaves found for this period</p>
              <p className="text-sm mt-1">
                {teamOnly ? 'Showing team leaves only' : 'Showing all employee leaves'}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
